import { enableDictation, isFeatureEnabled } from '@core/constant/featureFlags';
import { fireAndForget } from '@core/util/fireAndForget';
import CheckIcon from '@phosphor-icons/core/regular/check.svg?component-solid';
import MicrophoneIcon from '@phosphor-icons/core/regular/microphone.svg?component-solid';
import XIcon from '@phosphor-icons/core/regular/x.svg?component-solid';
import { Button } from '@ui';
import { Show } from 'solid-js';
import { match } from 'ts-pattern';
import type { DictationController } from '../core/types';
import { VolumeTimeline } from './volume-timeline';

export function DictationButton(props: {
  dictation: DictationController;
  disabled?: boolean;
}) {
  return (
    <Show when={isFeatureEnabled(enableDictation)}>
      <Button
        variant="ghost"
        size="icon-composer"
        class="text-ink not-touch:text-composer-ink"
        label={props.dictation.label()}
        tooltip={props.dictation.label()}
        disabled={props.disabled || props.dictation.disabled()}
        onClick={() => fireAndForget(props.dictation.start(), 'dictation')}
      >
        <MicrophoneIcon />
      </Button>
    </Show>
  );
}

export function DictationPanel(props: { dictation: DictationController }) {
  return (
    <Show when={props.dictation.active()}>
      <div
        class="absolute inset-0 z-10 flex items-center gap-2 rounded-[inherit] bg-composer px-[9.375px] text-composer-ink touch:gap-1.5 touch:bg-chrome touch:pr-2 touch:pl-3"
        role="group"
        aria-label="Dictation"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            props.dictation.cancel();
          }
        }}
      >
        <div class="flex min-w-0 flex-1 items-center gap-3 px-2 touch:gap-2 touch:px-0">
          <VolumeTimeline levels={props.dictation.volumeHistory()} />
          <span
            class="shrink-0 text-xs text-ink-muted"
            classList={{ 'sr-only': props.dictation.phase() === 'listening' }}
            role="status"
          >
            {match(props.dictation.phase())
              .with('starting', () => 'Starting…')
              .with('finishing', () => 'Finishing…')
              .with('review', () => 'Ready')
              .otherwise(() => 'Listening…')}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon-composer"
          class="text-composer-ink touch:bg-ink/5 touch:text-ink-muted"
          label="Cancel dictation"
          ref={(element) =>
            queueMicrotask(() => {
              if (element.isConnected) element.focus();
            })
          }
          onClick={props.dictation.cancel}
        >
          <XIcon />
        </Button>
        {/* Confirm is the composer's primary action while recording, so on
            touch it borrows the send button's filled circle. */}
        <Button
          variant="ghost"
          size="icon-composer"
          class="text-composer-ink touch:bg-ink touch:text-surface-4"
          label="Use dictation"
          disabled={
            props.dictation.phase() === 'starting' ||
            props.dictation.phase() === 'finishing'
          }
          onClick={() => fireAndForget(props.dictation.confirm(), 'dictation')}
        >
          <CheckIcon />
        </Button>
      </div>
    </Show>
  );
}

export function DictationFeedback(props: { dictation: DictationController }) {
  const message = () => props.dictation.message();
  return (
    <Show when={message()}>
      <p role="status" class="px-3 pt-1 text-xs text-ink-muted">
        {message()}
      </p>
    </Show>
  );
}
